const ATTRIBUTION_STORAGE_KEY = "referedData";
const ATTRIBUTION_VERSION = 2;
const ATTRIBUTION_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
// How long an affiliate IP captured earlier still counts as "this signup".
const AFFILIATE_IP_SESSION_MS = 30 * 60 * 1000;

const sanitizeReferralUrl = (
  value,
  baseUrl,
  URLConstructor = globalThis.URL,
) => {
  if (!value || typeof URLConstructor !== "function") {
    return "";
  }

  try {
    const url = new URLConstructor(value, baseUrl);
    if (!["http:", "https:"].includes(url.protocol)) {
      return "";
    }

    return `${url.protocol}//${url.host}${url.pathname}`;
  } catch {
    return "";
  }
};

const extractAttributionQueryValues = (
  search,
  keys,
  URLSearchParamsConstructor = globalThis.URLSearchParams,
) => {
  if (typeof URLSearchParamsConstructor !== "function") {
    return {};
  }

  const params = new URLSearchParamsConstructor(search);
  return keys.reduce((values, key) => {
    const value = params.get(key);
    if (value !== null) {
      values[key] = value;
    }
    return values;
  }, {});
};

const defaultCreateVisitorId = () => {
  if (globalThis.crypto && typeof globalThis.crypto.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }

  return `visitor-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const safeRemove = (storage) => {
  try {
    storage.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    // Attribution must never block signup when storage is unavailable.
  }
};

const readSignupAttribution = (
  storage,
  { nowMs = Date.now(), createVisitorId = defaultCreateVisitorId } = {},
) => {
  let stored;
  try {
    stored = JSON.parse(storage.getItem(ATTRIBUTION_STORAGE_KEY));
  } catch {
    safeRemove(storage);
    return {};
  }

  if (!stored || typeof stored !== "object" || Array.isArray(stored)) {
    return {};
  }

  const firstTouchMs = Date.parse(stored.dateTime);
  const explicitExpiryMs = stored.expiresAt
    ? Date.parse(stored.expiresAt)
    : firstTouchMs + ATTRIBUTION_MAX_AGE_MS;
  const maximumExpiryMs = firstTouchMs + ATTRIBUTION_MAX_AGE_MS;
  const effectiveExpiryMs = Math.min(explicitExpiryMs, maximumExpiryMs);

  if (
    !Number.isFinite(firstTouchMs) ||
    !Number.isFinite(effectiveExpiryMs) ||
    effectiveExpiryMs <= nowMs
  ) {
    safeRemove(storage);
    return {};
  }

  return {
    ...stored,
    visitorId: stored.visitorId || createVisitorId(),
    attributionVersion: ATTRIBUTION_VERSION,
    expiresAt: new Date(effectiveExpiryMs).toISOString(),
  };
};

const writeSignupAttribution = (
  storage,
  attribution,
  existing = {},
  { nowMs = Date.now(), createVisitorId = defaultCreateVisitorId } = {},
) => {
  const firstTouchMs = Number.isFinite(Date.parse(attribution.dateTime))
    ? Date.parse(attribution.dateTime)
    : nowMs;
  const maximumExpiryMs = firstTouchMs + ATTRIBUTION_MAX_AGE_MS;
  const existingExpiryMs = Date.parse(existing.expiresAt);
  const effectiveExpiryMs = Number.isFinite(existingExpiryMs)
    ? Math.min(existingExpiryMs, maximumExpiryMs)
    : maximumExpiryMs;
  const record = {
    ...existing,
    ...attribution,
    dateTime: new Date(firstTouchMs).toISOString(),
    visitorId: existing.visitorId || createVisitorId(),
    attributionVersion: ATTRIBUTION_VERSION,
    expiresAt: new Date(effectiveExpiryMs).toISOString(),
  };

  try {
    storage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Attribution must never block signup when storage is unavailable.
  }

  return record;
};

const collectSignupAttribution = ({
  storage,
  location,
  referrer = "",
  affiliateIp = "",
}) => {
  const sanitizeSignupReferralUrl = (value) =>
    sanitizeReferralUrl(value, location.origin);
  const data = {
    utm_content: "",
    utm_term: "",
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_id: "",
    gclid: "",
    gbraid: "",
    wbraid: "",
    fbclid: "",
    msclkid: "",
    li_fat_id: "",
    ttclid: "",
    initialReferer: "",
    dateTime: "",
    lastReferer: "",
    lastDateTime: "",
    initialLandingPage: "",
    lastLandingPage: "",
    lastPageBeforeSignup: "",
    lastUtmLandingPage: "",
    lastUtmDateTime: "",
    // Never read from the URL: a crafted link could otherwise credit an
    // affiliate for visitors who never clicked it (ClickUp z94kydf5tq).
    affiliateIp: "",
  };

  Object.assign(
    data,
    extractAttributionQueryValues(
      location.search,
      Object.keys(data).filter((key) => key !== "affiliateIp"),
    ),
  );

  const storeData = readSignupAttribution(storage);
  Object.keys(data).forEach((key) => {
    if (!data[key] && storeData[key]) {
      data[key] = storeData[key];
    }
  });
  // An IP follows the network, so it is not first-touch like the campaign tags:
  // a lookup replaces a stored address that is older than this signup session,
  // but not one captured moments ago. Within the session (an OAuth round trip,
  // a retry) the first address is the one nearest the affiliate click, even if
  // the network changed since. An unfinished lookup keeps whatever is stored.
  const nowMs = Date.now();
  const freshAffiliateIp =
    typeof affiliateIp === "string" ? affiliateIp.trim().slice(0, 45) : "";
  const storedAffiliateIpAtMs = Date.parse(storeData.affiliateIpAt);
  const storedAffiliateIpIsCurrent =
    Boolean(storeData.affiliateIp) &&
    Number.isFinite(storedAffiliateIpAtMs) &&
    nowMs - storedAffiliateIpAtMs >= 0 &&
    nowMs - storedAffiliateIpAtMs <= AFFILIATE_IP_SESSION_MS;
  let affiliateIpAt = storeData.affiliateIpAt || "";
  if (freshAffiliateIp && !storedAffiliateIpIsCurrent) {
    data.affiliateIp = freshAffiliateIp;
    affiliateIpAt = new Date(nowMs).toISOString();
  }

  const referedData = {};
  if (!data.initialReferer) {
    data.initialReferer = sanitizeSignupReferralUrl(referrer || location.href);
  }
  // Refresh navigation without replacing the acquisition source on OAuth return.
  data.lastReferer = sanitizeSignupReferralUrl(
    referrer || data.lastReferer || location.href,
  );
  if (!data.initialLandingPage) {
    data.initialLandingPage = sanitizeSignupReferralUrl(location.href);
  }
  data.lastLandingPage = sanitizeSignupReferralUrl(location.href);
  data.lastPageBeforeSignup = sanitizeSignupReferralUrl(location.href);
  if (!data.dateTime) {
    data.dateTime = new Date().toISOString();
  }
  data.lastDateTime = new Date().toISOString();

  const referedKeys = [
    "initialReferer",
    "dateTime",
    "lastReferer",
    "lastDateTime",
    "initialLandingPage",
    "lastLandingPage",
    "lastPageBeforeSignup",
    "lastUtmLandingPage",
    "lastUtmDateTime",
  ];

  referedKeys.forEach((key) => {
    if (
      key !== "dateTime" &&
      key !== "lastDateTime" &&
      key !== "lastUtmDateTime"
    ) {
      data[key] = sanitizeSignupReferralUrl(data[key]);
    }
    referedData[key] = data[key];
  });

  // `affiliateIpAt` stays in the browser record only; it is not sent.
  writeSignupAttribution(
    storage,
    { ...data, ...referedData, affiliateIpAt },
    storeData,
  );
  referedKeys.forEach((key) => delete data[key]);

  return { data, referedData };
};

const readGaClientId = (cookieString = "") => {
  const value = cookieString.split("; ").find((row) => row.startsWith("_ga="));

  if (!value) return null;

  const parts = value.split(".");

  // format: GA1.1.<part1>.<part2>
  let cid = parts.slice(-2).join(".");
  const sessionId = getGASessionId(cookieString);
  if (sessionId) {
    cid += `___${sessionId}`;
  }
  return cid;
};

const getGASessionId = (cookieString) => {
  const measurementId = "G-PCVBQ26MQM";
  const cookieName = `_ga_${measurementId.replace("G-", "")}`;
  const cookie = cookieString
    .split("; ")
    .find((row) => row.startsWith(cookieName + "="));

  if (!cookie) return null;

  const value = cookie.split("=")[1];

  const sessionStartMatch = value.match(/s(\d+)/);
  if (!sessionStartMatch) return null;

  return sessionStartMatch[1];
};

const clearSignupAttribution = (storage) => safeRemove(storage);

module.exports = {
  collectSignupAttribution,
  readGaClientId,
  ATTRIBUTION_MAX_AGE_MS,
  ATTRIBUTION_STORAGE_KEY,
  clearSignupAttribution,
  extractAttributionQueryValues,
  readSignupAttribution,
  sanitizeReferralUrl,
  writeSignupAttribution,
};
